/**
 * effect — the shell's effect vocabulary as an additive contract family
 * (FD22; register FD-CAN-22).
 *
 * The Founders shell's atmosphere and glass carry three intensities that
 * are design values, not component choices: the panel frost's blur radius
 * and re-saturation (FD-CAN-24 wears them), and the aurora ring's
 * edge-light level — how much of each section accent survives the mix
 * toward transparent before the ground shows through (FD-CAN-23 wears
 * it). This module owns that vocabulary the way `section-accent.ts` owns
 * the section colors: typed roles, Founders defaults from the design of
 * record, parse/clamp totality, and a projection to `--ncl-effect-*`
 * custom properties painted by the palette applier in every branch.
 *
 * The principal's light-mode word (2026-09-01: "we will create a light
 * mode at some point, so your translucent versus black is brilliant")
 * is why edgeLight is a mix-toward-TRANSPARENT weight and never a mix
 * toward black: the ground that shows through is the theme's own.
 *
 * Named absences: a `glow` role waits for the app's first glow recipe
 * (no wearer, no token); the user-facing tone-down knobs that will turn
 * these values live in the staked tone-down lineage's persistence seat.
 */

/**
 * FD31 (register FD-CAN-31; the principal's 2026-09-01 word on the live
 * Desk against the design of record: "that inner empty space obviously
 * isn't correct. I can see different shades" / "the Aurora background
 * seems a little different"): the vocabulary grows the shell group —
 *  - auroraBlur / auroraStart: the ring's blur radius and conic start
 *    angle (the design's 70px and 120°; the ring's stops are the
 *    FOUNDERS_AURORA_STOPS below, its per-stop alpha edgeLight);
 *  - shellRing / shellVignette: the shell's 1px inner ring (the design's
 *    13% white, mixed from the theme's text so light rises out of light)
 *    and the 80px inner vignette that makes the void read as depth;
 *  - glassFill: the panel glass's fill share (the design's .5 → .6 run
 *    top to bottom; the bottom stop is fill + 10);
 *  - cardGlow: the ringed card's silhouette bloom radius (the design's
 *    0 0 40px -12px accent bloom, carried as the drop-shadow radius).
 */
export const EFFECT_TOKEN_ROLES = [
  "glassBlur",
  "glassSaturate",
  "edgeLight",
  "auroraBlur",
  "auroraStart",
  "shellRing",
  "shellVignette",
  "glassFill",
  "cardGlow",
  "lightIntensity",
  "glowShare",
  "hoverLightAlpha",
  "hoverLightFloor",
  "doorFadeAlpha",
  "lightBreatheSeconds",
  "lightBreatheFloor",
  "lightConeAlpha",
  "lightConeBlur",
  "lightHaloAlpha",
  "lightHaloBlur",
  "lightShoulderAlpha",
  "lightPoolAlpha",
  "lightPoolShoulderAlpha",
  "lightRimAlpha",
  "lightRimShoulderAlpha",
  "lightRimGlowAlpha",
  "lightRimBlur",
  "lightEdgeAlpha",
  "lightWashAlpha",
  "lightWashBaseAlpha",
  "lightSpillAlpha",
  "lightSpillShoulderAlpha",
  "lightAmbientHaloAlpha",
  "lightAmbientHaloBlur",


] as const;

export type EffectTokenRole = (typeof EFFECT_TOKEN_ROLES)[number];

export type EffectTokenMap = Readonly<Record<EffectTokenRole, number>>;

/**
 * Per-role clamp bounds. Blur 0 is honest opacity and 40px is the
 * ceiling before frost turns to mush; saturation below 1 would drain
 * (tone-down collapses color elsewhere, never here) and above 2
 * cartoons; edge light is the accent's share of the mix, 0 (all ground)
 * to 100 (the opaque band FD-CAN-23 retired).
 */
export const EFFECT_TOKEN_BOUNDS: Readonly<Record<EffectTokenRole, readonly [number, number]>> =
  Object.freeze({
    glassBlur: [0, 40] as const,
    glassSaturate: [1, 2] as const,
    edgeLight: [0, 100] as const,
    // 0 is a hard-edged conic disc; past 160px the ring is a flat wash.
    auroraBlur: [0, 160] as const,
    auroraStart: [0, 360] as const,
    // The ring is a share of the theme's text color; 0 is no ring.
    shellRing: [0, 100] as const,
    shellVignette: [0, 200] as const,
    // 0 is invisible glass; 100 is opaque (the tone-down stance's shape).
    glassFill: [0, 100] as const,
    cardGlow: [0, 60] as const,
    lightIntensity: [0, 2] as const,
    glowShare: [0, 100] as const,
    hoverLightAlpha: [0, 100] as const,
    hoverLightFloor: [0, 100] as const,
    doorFadeAlpha: [0, 100] as const,
    lightBreatheSeconds: [1, 20] as const,
    lightBreatheFloor: [0, 1] as const,
    lightConeAlpha: [0, 100] as const,
    lightConeBlur: [0, 60] as const,
    lightHaloAlpha: [0, 100] as const,
    lightHaloBlur: [0, 60] as const,
    lightShoulderAlpha: [0, 100] as const,
    lightPoolAlpha: [0, 100] as const,
    lightPoolShoulderAlpha: [0, 100] as const,
    lightRimAlpha: [0, 1] as const,
    lightRimShoulderAlpha: [0, 1] as const,
    lightRimGlowAlpha: [0, 1] as const,
    lightRimBlur: [0, 10] as const,
    lightEdgeAlpha: [0, 100] as const,
    lightWashAlpha: [0, 100] as const,
    lightWashBaseAlpha: [0, 100] as const,
    lightSpillAlpha: [0, 100] as const,
    lightSpillShoulderAlpha: [0, 100] as const,
    lightAmbientHaloAlpha: [0, 100] as const,
    lightAmbientHaloBlur: [0, 100] as const,


  });

const EFFECT_TOKEN_UNITS: Readonly<Record<EffectTokenRole, string>> = Object.freeze({
  glassBlur: "px",
  glassSaturate: "",
  edgeLight: "%",
  auroraBlur: "px",
  auroraStart: "deg",
  shellRing: "%",
  shellVignette: "px",
  glassFill: "%",
  cardGlow: "px",
  lightIntensity: "",
  glowShare: "%",
  hoverLightAlpha: "%",
  hoverLightFloor: "%",
  doorFadeAlpha: "%",
  lightBreatheSeconds: "s",
  lightBreatheFloor: "",
  lightConeAlpha: "%",
  lightConeBlur: "px",
  lightHaloAlpha: "%",
  lightHaloBlur: "px",
  lightShoulderAlpha: "%",
  lightPoolAlpha: "%",
  lightPoolShoulderAlpha: "%",
  lightRimAlpha: "",
  lightRimShoulderAlpha: "",
  lightRimGlowAlpha: "",
  lightRimBlur: "px",
  lightEdgeAlpha: "%",
  lightWashAlpha: "%",
  lightWashBaseAlpha: "%",
  lightSpillAlpha: "%",
  lightSpillShoulderAlpha: "%",
  lightAmbientHaloAlpha: "%",
  lightAmbientHaloBlur: "px",


});

/**
 * Founders defaults — the design of record's intensities (its glass
 * carries blur with saturate(1.2–1.25); its ring stops sit at .44–.55
 * alpha over the ground). Do not tune these by eye; a change is a dated
 * design decision.
 */
export const FOUNDERS_EFFECT_TOKENS: EffectTokenMap = Object.freeze({
  // DATED 2026-09-01 (FD-CAN-31): 14 → 20, the design of record's panel
  // blur (its Cleo panel: blur(20px) saturate(1.2)); 14 was the pill blur.
  glassBlur: 20,
  glassSaturate: 1.2,
  edgeLight: 50,
  auroraBlur: 70,
  auroraStart: 120,
  shellRing: 13,
  shellVignette: 80,
  glassFill: 50,
  cardGlow: 20,
  lightIntensity: 1,
  glowShare: 100,
  hoverLightAlpha: 20,
  hoverLightFloor: 14,
  doorFadeAlpha: 50,
  lightBreatheSeconds: 5.5,
  lightBreatheFloor: 0.45,
  lightConeAlpha: 36,
  lightConeBlur: 28,
  lightHaloAlpha: 45,
  lightHaloBlur: 11,
  lightShoulderAlpha: 12,
  lightPoolAlpha: 44,
  lightPoolShoulderAlpha: 16,
  lightRimAlpha: .95,
  lightRimShoulderAlpha: .55,
  lightRimGlowAlpha: .55,
  lightRimBlur: 2.6,
  lightEdgeAlpha: 32,
  lightWashAlpha: 13,
  lightWashBaseAlpha: 6.5,
  lightSpillAlpha: 22,
  lightSpillShoulderAlpha: 8,
  lightAmbientHaloAlpha: 38,
  lightAmbientHaloBlur: 34,


});

/**
 * FD31: the aurora's own spectrum — the design of record's eight ring
 * hues (its `conic-gradient(from 120deg …)` at `neucleos Cockpit.dc.html`
 * line 29), converted sRGB → OKLCH. The section-accent family cannot
 * paint this ring: it carries five of these hues and none of the red,
 * orange, yellow, or teal passes, so the rim read green-blue-pink on the
 * live Desk. The ring reads `--ncl-aurora-1 … -8`; edgeLight is each
 * stop's share toward transparent. Named absence: a per-theme spectrum
 * (every theme paints the Founders stops today; `resolveAuroraStops`
 * is the parse door a theme's own stops will enter by).
 */
export interface AuroraStop {
  readonly lightness: number;
  readonly chroma: number;
  readonly hue: number;
}

export const AURORA_STOP_COUNT = 8;

export const FOUNDERS_AURORA_STOPS: readonly AuroraStop[] = Object.freeze([
  Object.freeze({ lightness: 0.667, chroma: 0.22, hue: 25.5 }), // #FF4747 red
  Object.freeze({ lightness: 0.783, chroma: 0.168, hue: 66.2 }), // #FF9F1C orange
  Object.freeze({ lightness: 0.843, chroma: 0.167, hue: 83.5 }), // #FFC021 yellow
  Object.freeze({ lightness: 0.774, chroma: 0.195, hue: 151 }), // #2FD771 green
  Object.freeze({ lightness: 0.836, chroma: 0.14, hue: 185.6 }), // #2EE6D6 teal
  Object.freeze({ lightness: 0.741, chroma: 0.15, hue: 239.6 }), // #38B6FF blue
  Object.freeze({ lightness: 0.651, chroma: 0.211, hue: 295.5 }), // #9D6BFF violet
  Object.freeze({ lightness: 0.672, chroma: 0.233, hue: 2.3 }), // #FF3D8A pink
]);

export function auroraStopCssVariable(index: number): string {
  return `--ncl-aurora-${index + 1}`;
}

/**
 * Parse persisted or untrusted stops into exactly AURORA_STOP_COUNT
 * entries: a malformed or absent stop falls back to the Founders stop at
 * that index, so the projection is always total.
 */
export function parseAuroraStops(value: unknown): readonly AuroraStop[] {
  const source = Array.isArray(value) ? value : [];
  const stops: AuroraStop[] = [];
  for (let index = 0; index < AURORA_STOP_COUNT; index += 1) {
    const fallback = FOUNDERS_AURORA_STOPS[index]!;
    const raw = record(source[index]);
    const lightness = finiteNumber(raw.lightness, fallback.lightness);
    const chroma = finiteNumber(raw.chroma, fallback.chroma);
    const hue = finiteNumber(raw.hue, fallback.hue);
    stops.push(
      Object.freeze({
        lightness: Math.min(0.95, Math.max(0.3, lightness)),
        chroma: Math.min(0.3, Math.max(0.02, chroma)),
        hue: Math.round((((hue % 360) + 360) % 360) * 1000) / 1000,
      }),
    );
  }
  return Object.freeze(stops);
}

/** Project the stops to `--ncl-aurora-N` → `oklch(l c h)`. */
export function resolveAuroraStops(value?: unknown): Readonly<Record<string, string>> {
  const stops = value === undefined ? FOUNDERS_AURORA_STOPS : parseAuroraStops(value);
  const cssVariables: Record<string, string> = {};
  stops.forEach((stop, index) => {
    cssVariables[auroraStopCssVariable(index)] =
      `oklch(${compactNumber(stop.lightness)} ${compactNumber(stop.chroma)} ${compactNumber(stop.hue)})`;
  });
  return Object.freeze(cssVariables);
}

function finiteNumber(value: unknown, fallback: number): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

function record(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : {};
}

function compactNumber(value: number): string {
  return String(Math.round(value * 100_000) / 100_000);
}

export function clampEffectToken(role: EffectTokenRole, value: unknown): number {
  const [floor, ceiling] = EFFECT_TOKEN_BOUNDS[role];
  const parsed = finiteNumber(value, FOUNDERS_EFFECT_TOKENS[role]);
  return Math.min(ceiling, Math.max(floor, parsed));
}

/**
 * Parse persisted or untrusted data into the full role map. Unknown keys
 * are dropped; absent or malformed roles fall back to the Founders
 * default for that role — the map is always total.
 */
export function parseEffectTokens(value: unknown): EffectTokenMap {
  const source = record(value);
  const parsed = {} as Record<EffectTokenRole, number>;
  for (const role of EFFECT_TOKEN_ROLES) {
    parsed[role] = clampEffectToken(role, source[role]);
  }
  return Object.freeze(parsed);
}

const kebab = (role: string): string => role.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

export function effectTokenCssVariable(role: EffectTokenRole): string {
  return `--ncl-effect-${kebab(role)}`;
}

export interface ResolvedEffectTokens {
  readonly tokens: EffectTokenMap;
  /** `--ncl-effect-<kebab-role>` → the value with its unit, e.g. `14px`. */
  readonly cssVariables: Readonly<Record<string, string>>;
}

/** Deterministically project a role map to its CSS custom properties. */
export function resolveEffectTokens(value?: unknown): ResolvedEffectTokens {
  const tokens = value === undefined ? FOUNDERS_EFFECT_TOKENS : parseEffectTokens(value);
  const cssVariables: Record<string, string> = {};
  for (const role of EFFECT_TOKEN_ROLES) {
    cssVariables[effectTokenCssVariable(role)] =
      `${compactNumber(tokens[role])}${EFFECT_TOKEN_UNITS[role]}`;
  }
  return Object.freeze({ tokens, cssVariables: Object.freeze(cssVariables) });
}
